import React, { useState, useEffect, useRef } from 'react';
import { FaSearch, FaUser, FaTimes } from 'react-icons/fa';
import axiosInstance from '../api/axiosInstance';

const UserSearch = ({ 
  selectedUser, 
  onUserSelect, 
  placeholder = "Nach Username oder E-Mail suchen...",
  disabled = false 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim().length >= 2) {
        searchUsers(searchTerm);
      } else {
        setSearchResults([]);
        setShowDropdown(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        !searchRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchUsers = async (query) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/admin/users/search?q=${encodeURIComponent(query)}&limit=8`);
      setSearchResults(response.data.users || []);
      setShowDropdown(true);
    } catch (error) {
      console.error('User-Suche Fehler:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = (user) => {
    onUserSelect(user);
    setSearchTerm('');
    setShowDropdown(false);
    setSearchResults([]);
  };

  const handleClearSelection = () => {
    onUserSelect(null);
    setSearchTerm('');
    setShowDropdown(false);
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: <span className="badge badge-success badge-sm">Aktiv</span>,
      inactive: <span className="badge badge-warning badge-sm">Inaktiv</span>,
      banned: <span className="badge badge-error badge-sm">Gesperrt</span>
    };
    return badges[status] || null;
  };

  return (
    <div className="relative w-full">
      {/* Selected User Display */}
      {selectedUser && (
        <div className="flex items-center gap-3 p-3 bg-base-200 rounded-lg border-2 border-primary">
          <div className="avatar">
            <div className="w-8 h-8 rounded-full">
              {selectedUser.avatarUrl ? (
                <img src={selectedUser.avatarUrl} alt={selectedUser.username} />
              ) : (
                <div className="bg-primary flex items-center justify-center">
                  <FaUser className="text-primary-content text-sm" />
                </div>
              )}
            </div>
          </div>
          <div className="flex-1">
            <div className="font-medium text-sm">{selectedUser.username}</div>
            <div className="text-xs text-base-content/60">{selectedUser.email}</div>
          </div>
          {getStatusBadge(selectedUser.status)}
          <button
            type="button"
            onClick={handleClearSelection}
            className="btn btn-ghost btn-xs"
            disabled={disabled}
          >
            <FaTimes />
          </button>
        </div>
      )}

      {/* Search Input */}
      {!selectedUser && (
        <div className="relative">
          <div className="relative">
            <input
              ref={searchRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={placeholder}
              className="input input-bordered w-full pl-10"
              disabled={disabled}
              onFocus={() => {
                if (searchResults.length > 0) {
                  setShowDropdown(true);
                }
              }}
            />
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/40" />
            {loading && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <span className="loading loading-spinner loading-sm"></span>
              </div>
            )}
          </div>

          {/* Search Results Dropdown */}
          {showDropdown && searchResults.length > 0 && (
            <div
              ref={dropdownRef}
              className="absolute top-full left-0 right-0 bg-base-100 border border-base-300 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto mt-1"
            >
              {searchResults.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => handleSelectUser(user)}
                  className="w-full p-3 text-left hover:bg-base-200 flex items-center gap-3 border-b border-base-300 last:border-b-0"
                >
                  <div className="avatar">
                    <div className="w-8 h-8 rounded-full">
                      {user.avatarUrl ? (
                        <img src={user.avatarUrl} alt={user.username} />
                      ) : (
                        <div className="bg-neutral flex items-center justify-center">
                          <FaUser className="text-neutral-content text-sm" />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{user.username}</div>
                    <div className="text-xs text-base-content/60 truncate">{user.email}</div>
                  </div>
                  {getStatusBadge(user.status)}
                </button>
              ))}
            </div>
          )}

          {/* No Results */}
          {showDropdown && searchResults.length === 0 && searchTerm.length >= 2 && !loading && (
            <div
              ref={dropdownRef}
              className="absolute top-full left-0 right-0 bg-base-100 border border-base-300 rounded-lg shadow-lg z-50 mt-1"
            >
              <div className="p-4 text-center text-base-content/60">
                Keine User gefunden für "{searchTerm}"
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserSearch;
